// IframeViewer.jsx
import React, { useState, useMemo, useLayoutEffect, useRef } from 'react';
import { useStore } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

import Loading from '../component/Loading.jsx';
import RichIframe from '../component/RichIframe.jsx';
import trackMixpanel from '../resource/mixpanel.js';
import getOperationData from '../selector/getOperationData.js';

import { EMPTY_ARRAY, NULL_FUNCTION } from '../resource/defaults.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { Entrypoint } from '../resource/shopConstants.js';
import { getIsInBrowserMainThread } from '../resource/getJsEnvironment.js';

import { headerHeight, color } from '../style/variables.js';

import replaceTemplate from '../resource/replaceTemplate.js';
import { getFpIdPro } from '../resource/fpjs.js';
import { decodeUnicode } from '../resource/base64Utils.js';
import { iframe as iframeDebug } from '../resource/debug.js';

const log = iframeDebug.extend('log:[IframeViewer]');
const errorLog = iframeDebug.extend('error:[IframeViewer]');

const parseJSON = value => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

// Decode the `campaign` param and return its target modalId, or null.
const getCampaignModalId = url => {
  const campaign = parseJSON(
    decodeUnicode({ str: url.searchParams.get('campaign') || '' })
  );
  return campaign?.modalId ?? null;
};

/**
 * check if the url is same origin
 * @param {URL} url - the url to check
 * @returns {boolean}
 */
const isSameOrigin = url => {
  if (!getIsInBrowserMainThread()) return false;
  return url.origin.toLowerCase() === window.location.origin.toLowerCase();
};

/**
 * Validates if the current URL matches the allowed URL pattern, including origin and query parameter checks.
 *
 * @param {URL} currentUrl - The current URL object to validate
 * @param {string} allowedUrl - The allowed URL pattern string
 * @param {string} [meId] - Optional user ID for specific value validation
 * @returns {boolean} Returns true if the current URL matches the allowed pattern, false otherwise
 * @param {string} iframeUrlPrefix - The prefix of the iframe URL
 *
 * @description
 * This function performs the following validations:
 * 1. Checks if URL origins match (case-insensitive)
 * 2. If the allowed URL has no query parameters, any URL with matching origin is valid
 * 3. Special handling for Looker Studio's params JSON parameter
 * 4. Validates all query parameters against allowed values
 *
 */
const validateURL = (currentUrl, allowedUrl) => {
  const parsedAllowed = new URL(allowedUrl);

  // Check if origins match (case insensitive)
  if (currentUrl.origin.toLowerCase() !== parsedAllowed.origin.toLowerCase()) {
    return false;
  }

  // Get allowed and current parameters
  const allowedParams = new URLSearchParams(parsedAllowed.search);
  const currentParams = new URLSearchParams(currentUrl.search);

  // If allowed URL has no search params, any URL with matching origin is valid
  if (parsedAllowed.search === '') {
    return true;
  }

  // Handle special params JSON case for Looker Studio
  const allowedParamsJSON = allowedParams.get('params');
  if (allowedParamsJSON) {
    try {
      const allowedJSON = parseJSON(allowedParamsJSON);
      const currentJSON = parseJSON(currentParams.get('params'));

      if (!allowedJSON || !currentJSON) return false;

      return Object.keys(allowedJSON).every(key =>
        Object.prototype.hasOwnProperty.call(currentJSON, key)
      );
    } catch {
      return false;
    }
  }

  // Handle empty parameter values (e.g., lang=)
  for (const [key, allowedValue] of allowedParams.entries()) {
    const currentValue = currentParams.get(key);

    // If allowed value is empty, any value for this parameter is valid
    if (allowedValue === '') {
      continue;
    }

    // If allowed value is specified, current value must match (case insensitive)
    if (
      !currentValue ||
      currentValue.toLowerCase() !== allowedValue.toLowerCase()
    ) {
      return false;
    }
  }

  return true;
};

export const IframeViewer = ({
  meId = '',
  allowURL = EMPTY_ARRAY,
  bypassProxyURL = EMPTY_ARRAY,
  push = NULL_FUNCTION,
  replace = NULL_FUNCTION,
  propsSourceUrl = '',
  iframeUrlPrefix = '',
  iframeStaticUrlPrefix = '',
  shouldShowHeader = true,
  meToken = '',
  tokenWhitelistDomains = EMPTY_ARRAY,
  toggleModal = NULL_FUNCTION,
  apiUrlPrefix = '',
  publicUrlEncryptKeys = '',
  iconBaseUrl = '',
  domainTrackerLabel = '',
  domainTrackerType = '',
  isDomainTrackerFetched = false,
  isRemoteConfigMerged = false,
  fetchDomainTrackerResource = NULL_FUNCTION,
  checkRiskGate = NULL_FUNCTION,
  addvalue = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasIframeError, setHasIframeError] = useState(false);
  const [iframeErrorUrl, setIframeErrorUrl] = useState('');
  const [isIframeReady, setIsIframeReady] = useState(false);
  const [iframeReadyCount, setIframeReadyCount] = useState(0);
  const { search } = useLocation();
  const iframeSourceRef = useRef(null);
  const iframeOriginRef = useRef(null);
  const hasRedirectedRef = useRef(false);
  const store = useStore();

  const sourceUrlData = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    let source = propsSourceUrl || searchParams.get('source');

    if (!source || !isRemoteConfigMerged) return { url: null };

    // if source is a relative path, we need to use iframeStaticUrlPrefix to build the full URL
    if (!source.startsWith('http') && iframeStaticUrlPrefix) {
      source = new URL(source, iframeStaticUrlPrefix).href;
    }

    try {
      const decodedSource = decodeURIComponent(source);
      const templatedUrl = replaceTemplate({
        string: decodedSource,
        data: { USER_ID: meId || '' },
      });

      const url = new URL(templatedUrl);

      // Merge outer-level Google Form prefill params (entry.*) into source URL
      const outerParams = new URLSearchParams(search);
      outerParams.forEach((value, key) => {
        if (key.startsWith('entry.') && !url.searchParams.has(key)) {
          url.searchParams.set(key, value);
        }
      });

      // Find any matching allowed URL or same origin
      const isAllowed =
        isSameOrigin(url) ||
        allowURL.some(allowedUrl => validateURL(url, allowedUrl));

      // allowURL not yet loaded from remote config, keep loading
      if (!isAllowed && allowURL.length === 0) {
        return { url: null };
      }

      if (!isAllowed) {
        errorLog('[iframe] url not in allowURL list', { url: url.href });
        return { url: '', hasError: true, errorUrl: url.href };
      }

      return { url };
    } catch (error) {
      errorLog('[iframe] failed to parse source url', {
        source,
        error: error.message,
      });
      return { url: null, hasError: true, errorUrl: source };
    }
  }, [
    search,
    meId,
    allowURL,
    propsSourceUrl,
    iframeStaticUrlPrefix,
    isRemoteConfigMerged,
  ]);

  const sourceUrl = sourceUrlData.url ?? null;
  const hasError = !!sourceUrlData.hasError || hasIframeError;
  const errorUrl = sourceUrlData.errorUrl || iframeErrorUrl;

  const shouldSendToken = useMemo(() => {
    if (
      !sourceUrl ||
      !tokenWhitelistDomains ||
      tokenWhitelistDomains.length === 0
    )
      return false;

    try {
      const sourceDomain = sourceUrl.hostname.toLowerCase();

      // Check if sourceUrl domain matches any tokenWhitelistDomains domain or is a subdomain
      return tokenWhitelistDomains.some(domain => {
        try {
          const whitelistUrl = new URL(domain);
          const whitelistDomain = whitelistUrl.hostname.toLowerCase();

          return (
            sourceDomain === whitelistDomain ||
            sourceDomain.endsWith('.' + whitelistDomain)
          );
        } catch (_) {
          return false;
        }
      });
    } catch (_) {
      return false;
    }
  }, [sourceUrl, tokenWhitelistDomains]);

  useLayoutEffect(() => {
    if (!iconBaseUrl && domainTrackerLabel && !isDomainTrackerFetched) {
      fetchDomainTrackerResource({
        resourceType: domainTrackerType,
      });
    }
  }, [
    iconBaseUrl,
    domainTrackerLabel,
    isDomainTrackerFetched,
    fetchDomainTrackerResource,
    domainTrackerType,
  ]);

  // Listen for MIXPANEL_TRACK from iframe and forward to Mixpanel
  useLayoutEffect(() => {
    const handleMixpanelTrack = event => {
      if (event?.data?.type !== 'MIXPANEL_TRACK') return;
      const { event: eventName, properties } = event.data;
      if (!eventName) return;
      trackMixpanel({ type: eventName, payload: { ...properties } });
    };

    window.addEventListener('message', handleMixpanelTrack);
    return () => window.removeEventListener('message', handleMixpanelTrack);
  }, []);

  // Listen for IFRAME_READY - only set up listener once
  useLayoutEffect(() => {
    const onReady = event => {
      if (event?.data?.type !== 'IFRAME_READY') return;

      // Store iframe source and origin for later use
      const source = event.source; // the iframe's WindowProxy, for postMessage back
      const targetOrigin = event.origin || '*'; // e.g. 'https://looker.swag.live'
      iframeSourceRef.current = source;
      iframeOriginRef.current = targetOrigin;
      setIsIframeReady(true);
      setIsLoading(false);
      setIframeReadyCount(count => count + 1);

      // Run the risk gate only when the iframe asks for it; fail closed.
      if (!event.data.needsRiskGate) return;
      const { riskCheckTriggerType } = event.data;
      (async () => {
        try {
          await checkRiskGate({ triggeredType: riskCheckTriggerType });
          const { status = 'blocked' } =
            getOperationData(store.getState(), ['riskGate']) || {};
          const fpid = await getFpIdPro();
          source.postMessage(
            { type: 'RISK_GATE_RESULT', status, fpid },
            targetOrigin
          );
        } catch (error) {
          errorLog('[iframe] risk gate threw', { error: error?.message });
          source.postMessage(
            { type: 'RISK_GATE_RESULT', status: 'blocked', fpid: null },
            targetOrigin
          );
        }
      })();
    };

    window.addEventListener('message', onReady);
    return () => {
      window.removeEventListener('message', onReady);
    };
  }, [checkRiskGate, store]);

  // Send domain config and token when iframe is ready
  useLayoutEffect(() => {
    if (isIframeReady && iframeSourceRef.current && shouldSendToken) {
      const targetOrigin = iframeOriginRef.current || '*';

      // Send domain config first
      if (iconBaseUrl && apiUrlPrefix && publicUrlEncryptKeys) {
        iframeSourceRef.current.postMessage(
          {
            type: 'SET_DOMAIN_CONFIG',
            iconBaseUrl,
            apiUrlPrefix,
            publicUrlEncryptKeys,
          },
          targetOrigin
        );
      }

      // Then send token
      iframeSourceRef.current.postMessage(
        { type: 'SET_TOKEN', token: meToken || '' },
        targetOrigin
      );
    }
  }, [
    iframeReadyCount,
    isIframeReady,
    shouldSendToken,
    meToken,
    iconBaseUrl,
    apiUrlPrefix,
    publicUrlEncryptKeys,
  ]);

  // Listen to PWA_NAVIGATE, IFRAME_NAVIGATE message to navigate to the target URL
  useLayoutEffect(() => {
    const handleIframeNavigate = event => {
      if (
        ['PWA_NAVIGATE', 'IFRAME_NAVIGATE'].includes(event?.data?.type) &&
        event.data.url
      ) {
        try {
          const targetUrl = new URL(event.data.url);

          // Close IframeModal before navigating, but skip when reopening another
          // IframeModal — closing it would kill the one just reopened.
          const isReopeningIframeModal =
            targetUrl.searchParams.get('campaign-id') === 'open-modal' &&
            getCampaignModalId(targetUrl) === 'IframeModal';
          if (!isReopeningIframeModal) {
            toggleModal({ modalId: 'IframeModal', isOpen: false });
          }

          const redirectUrl = new URL(
            targetUrl.pathname + targetUrl.search + targetUrl.hash,
            window.location.origin
          );

          push(redirectUrl.pathname + redirectUrl.search + redirectUrl.hash);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            'Failed to parse navigation URL on IframeViewer:',
            event.data.url,
            error
          );
        }
      }
    };

    window.addEventListener('message', handleIframeNavigate);
    return () => {
      window.removeEventListener('message', handleIframeNavigate);
    };
  }, [toggleModal, push]);

  useLayoutEffect(() => {
    const handleAddvalue = event => {
      if (event?.data?.type !== 'ADDVALUE') return;
      const { entrypoint = Entrypoint.ADDVALUE, via = 'direct' } = event.data;

      addvalue({ entrypoint, via });
    };

    window.addEventListener('message', handleAddvalue);
    return () => window.removeEventListener('message', handleAddvalue);
  }, [addvalue]);

  if (sourceUrl === '') {
    if (!hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      replace('/');
    }
    return null;
  }

  if (!sourceUrl) {
    return null;
  }

  const sourceDomain = sourceUrl.host;
  const isClient = typeof window !== 'undefined';

  // Iframe will not able to open on client side render environments, so we need a remote config to change to SSR upstream domains.
  const baseUrl = iframeUrlPrefix || (isClient ? location.origin : '');
  const proxyUrl = baseUrl ? new URL('iframeProxy', baseUrl) : null;

  if (proxyUrl) {
    proxyUrl.searchParams.set('url', sourceUrl.href);
  }

  const shouldBypassProxy =
    bypassProxyURL.includes(sourceDomain) ||
    (iframeStaticUrlPrefix &&
      sourceDomain === new URL(iframeStaticUrlPrefix).host);

  const finalIframeSrc =
    shouldBypassProxy || !proxyUrl ? sourceUrl.href : proxyUrl.href;

  if (shouldBypassProxy) {
    log('bypassing proxy', { sourceDomain, finalSrc: finalIframeSrc });
  } else if (proxyUrl) {
    log('using proxy', {
      sourceDomain,
      proxyUrl: proxyUrl.href,
      iframeUrlPrefix,
    });
  } else {
    errorLog('no proxy available and not in bypass list', {
      sourceDomain,
      iframeUrlPrefix,
    });
  }

  const isWaitingForDomainConfig =
    domainTrackerLabel && !iconBaseUrl && !isDomainTrackerFetched;

  return (
    <StyledIframeViewer shouldShowHeader={shouldShowHeader}>
      {(isLoading || isWaitingForDomainConfig) && !hasError && (
        <LoadingWrapper>
          <Loading size={62} />
        </LoadingWrapper>
      )}
      {hasError && (
        <ErrorContainer>
          <ErrorMessage>
            URL
            {t('invalid_format', {
              ns: TranslationNamespace.SETTINGS,
            })}
          </ErrorMessage>
          <ErrorMessageContent>{errorUrl}</ErrorMessageContent>
        </ErrorContainer>
      )}
      <StyledRichIframe
        src={finalIframeSrc}
        style={{ display: hasError ? 'none' : 'block' }}
        onLoad={() => {
          setIsLoading(false);
        }}
        onError={() => {
          setIsLoading(false);
          setHasIframeError(true);
          setIframeErrorUrl(sourceUrl.href);
        }}
      />
    </StyledIframeViewer>
  );
};

IframeViewer.propTypes = {
  meId: PropTypes.string,
  allowURL: PropTypes.array,
  bypassProxyURL: PropTypes.array,
  push: PropTypes.func,
  replace: PropTypes.func,
  propsSourceUrl: PropTypes.string,
  iframeUrlPrefix: PropTypes.string,
  iframeStaticUrlPrefix: PropTypes.string,
  shouldShowHeader: PropTypes.bool,
  meToken: PropTypes.string,
  tokenWhitelistDomains: PropTypes.array,
  toggleModal: PropTypes.func,
  apiUrlPrefix: PropTypes.string,
  publicUrlEncryptKeys: PropTypes.string,
  iconBaseUrl: PropTypes.string,
  domainTrackerLabel: PropTypes.string,
  domainTrackerType: PropTypes.string,
  isDomainTrackerFetched: PropTypes.bool,
  isRemoteConfigMerged: PropTypes.bool,
  fetchDomainTrackerResource: PropTypes.func,
  checkRiskGate: PropTypes.func,
  addvalue: PropTypes.func,
};

const StyledIframeViewer = styled.div`
  display: flex;
  width: 100%;
  height: ${({ shouldShowHeader }) =>
    shouldShowHeader ? `calc(100vh - ${headerHeight}px)` : '100%'};
  height: ${({ shouldShowHeader }) =>
    shouldShowHeader ? `calc(100dvh - ${headerHeight}px)` : '100%'};
  position: relative;
  ${({ shouldShowHeader }) =>
    shouldShowHeader && `margin-top: ${headerHeight}px;`}
`;

const LoadingWrapper = styled.div`
  width: 100%;
  position: absolute;
  height: calc(100% - ${headerHeight}px);
  inset: 0;
  z-index: 2;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: ${color.neutral[100]};
  font-size: 16px;
  text-align: center;
  padding: 20px;
`;

const ErrorMessageContent = styled.div`
  color: ${color.neutral[100]};
  font-size: 16px;
  text-align: center;
  padding: 20px;
`;

const StyledRichIframe = styled(RichIframe)`
  width: 100%;
  height: 100%;
  max-width: 768px;
  margin: auto;
`;

export default IframeViewer;
